import { useEffect, useState, useCallback, useRef } from "react";
import api, { API } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { HelpHint } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { FeedbackPrompt } from "@/context/FeedbackContext";
import { InfoTip } from "@/components/InfoTip";
import { ShareImagePrivacy } from "@/components/ShareImagePrivacy";
import {
  Share2, Copy, Loader2, EyeOff, CheckCircle2, Ban, Plus, ChevronDown,
  FileText, ShieldCheck, Lock, AlertTriangle, Images, ChevronRight, Eye, MessageSquareWarning, Activity, UserCheck,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : "");

export function ShareLinkManager({ propertyId, isSample = false }) {
  const { isPremium, openUpgrade } = useTier();
  const [links, setLinks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [resending, setResending] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/share-links/${propertyId}`);
      setLinks(data);
    } catch (e) { console.error("share-links load failed", e); /* keep prior list */ }
    try {
      const { data } = await api.get(`/properties/${propertyId}/link-events`);
      setEvents(data);
    } catch (e) { console.error("link-events load failed", e); }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const create = async (linkType) => {
    if (linkType === "witness" && !isPremium) { openUpgrade("witness_link"); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/share-links`, { property_id: propertyId, link_type: linkType });
      await load();
      try { await navigator.clipboard.writeText(data.share_url); toast.success("Link created & copied"); }
      catch { toast.success("Link created"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (linkId) => {
    try { await api.post(`/share-links/${linkId}/revoke`); await load(); toast.success("Link revoked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  const resend = async (linkId) => {
    setResending(linkId);
    try { await api.post(`/share-links/${linkId}/resend-notification`); await load(); toast.success("Email resent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend email"); }
    finally { setResending(null); }
  };

  const copy = async (link) => {
    const path = link.link_type === "baseline_agreement" ? "baseline" : link.link_type === "witness" ? "witness" : "report";
    const url = `${window.location.origin}/${path}/${link.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("Copied!"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] p-4" data-testid="share-link-manager">
      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Share2 size={18} className="text-brand mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy flex flex-wrap items-center gap-1">Share this documentation with your landlord <InfoTip topic="shareDocumentation" /> <HelpHint topic="share" /></p>
            <p className="text-xs text-[#475569]">Unguessable links. No login needed to view. Revoke anytime.</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="create-share-link-btn" disabled={creating}
              className="rounded-xl bg-navy hover:bg-navy-hover text-white shrink-0 w-full sm:w-auto">
              {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
              Create link <ChevronDown size={15} className="ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem data-testid="create-report-link" onClick={() => create("report")} className="cursor-pointer">
              <FileText size={16} className="mr-2 text-brand" />
              <div><p className="text-sm font-medium">Share full report</p><p className="text-[11px] text-[#94A3B8]">Read-only condition report</p></div>
            </DropdownMenuItem>
            {isSample ? (
              <DropdownMenuItem data-testid="create-baseline-link-disabled" disabled title="Not available on sample properties — add your own place to use this." className="opacity-60 cursor-not-allowed">
                <ShieldCheck size={16} className="mr-2 text-[#94A3B8]" />
                <div><p className="text-sm font-medium">Request baseline acknowledgment</p><p className="text-[11px] text-[#94A3B8]">Not available on sample properties — add your own place to use this.</p></div>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem data-testid="create-baseline-link" onClick={() => create("baseline_agreement")} className="cursor-pointer">
                <ShieldCheck size={16} className="mr-2 text-brand" />
                <div><p className="text-sm font-medium">Request baseline acknowledgment</p><p className="text-[11px] text-[#94A3B8]">Landlord signs the move-in baseline</p></div>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid="create-witness-link" onClick={() => create("witness")} className="cursor-pointer">
              <UserCheck size={16} className="mr-2 text-brand" />
              <div><p className="text-sm font-medium flex items-center gap-1">Invite a witness {!isPremium && <Lock size={11} className="text-[#94A3B8]" />}</p><p className="text-[11px] text-[#94A3B8]">A friend records they saw your documentation</p></div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-3 space-y-2">
        {creating && <Skeleton className="h-16 rounded-xl" data-testid="share-link-skeleton" />}
        {!loading && links.length === 0 && !creating && (
          <p className="text-xs text-[#94A3B8]">No links yet. Create one to share this property.</p>
        )}
        {links.map((l) => l.link_type === "baseline_agreement"
          ? <BaselineCard key={l.link_id} link={l} onCopy={copy} onRevoke={revoke}
              onResend={resend} resending={resending === l.link_id}
              expanded={expanded === l.link_id} onToggle={() => setExpanded(expanded === l.link_id ? null : l.link_id)} />
          : l.link_type === "witness"
          ? <WitnessCard key={l.link_id} link={l} events={events} onCopy={copy} onRevoke={revoke} />
          : l.link_type === "maintenance_view"
          ? null
          : <ReportCard key={l.link_id} link={l} events={events} onCopy={copy} onRevoke={revoke} />
        )}
      </div>
      {links.some((l) => l.link_type === "baseline_agreement" && l.landlord_signed) && (
        <FeedbackPrompt context="baseline_signed" />
      )}
      <ActivityFeed events={events} />
    </div>
  );
}

function ActivityFeed({ events }) {
  const [open, setOpen] = useState(false);
  if (!events || events.length === 0) return null;
  return (
    <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-white" data-testid="link-activity-feed">
      <button data-testid="toggle-activity-feed" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-3 text-left">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
          <Activity size={15} className="text-brand" /> Recipient activity
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#EEF2F6] text-[11px] font-semibold text-[#475569]">{events.length}</span>
        </span>
        <ChevronRight size={16} className={`text-[#94A3B8] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <ul className="border-t border-[#E2E8F0] divide-y divide-[#F1F5F9]" data-testid="activity-feed-list">
          {events.map((e) => <ActivityRow key={e.event_id} e={e} />)}
        </ul>
      )}
    </div>
  );
}

function ActivityRow({ e }) {
  const who = (e.signer_name || "").trim();
  const section = e.item_label || e.item_ref;
  const label = e.response_label || "Response";
  const isResponse = ["item_acknowledged", "item_disputed", "item_response"].includes(e.event_type);
  let icon, text;
  if (e.event_type === "opened") {
    icon = <Eye size={14} className="text-[#1E40AF] mt-0.5 shrink-0" />;
    text = <span>Link <b>opened</b> by recipient</span>;
  } else if (e.event_type === "attested") {
    icon = <UserCheck size={14} className="text-[#177245] mt-0.5 shrink-0" />;
    text = <span>{who ? <b>{who}</b> : "A witness"} recorded a statement</span>;
  } else if (e.event_type === "noted") {
    icon = <MessageSquareWarning size={14} className="text-[#92400E] mt-0.5 shrink-0" />;
    text = <span>{who ? <b>{who}</b> : "Recipient"} added a note on <b>{section}</b></span>;
  } else if (isResponse) {
    const disagree = label === "Disagreed" || label === "Could not verify";
    icon = disagree
      ? <MessageSquareWarning size={14} className="text-[#92400E] mt-0.5 shrink-0" />
      : <CheckCircle2 size={14} className="text-[#475569] mt-0.5 shrink-0" />;
    text = <span>{who ? <b>{who}</b> : "Recipient"} responded <b>{label}</b> on <b>{section}</b></span>;
  } else {
    icon = <MessageSquareWarning size={14} className="text-[#92400E] mt-0.5 shrink-0" />;
    text = <span>{who ? <b>{who}</b> : "Recipient"} responded on <b>{section}</b></span>;
  }
  return (
    <li className="px-3.5 py-2.5 flex items-start gap-2.5" data-testid={`activity-row-${e.event_type}`}>
      {icon}
      <div className="min-w-0">
        <p className="text-xs text-[#334155]">
          {text}
          {e.superseded && <span className="ml-1.5 text-[10px] text-[#94A3B8]">(superseded)</span>}
        </p>
        {e.note && <p className="mt-0.5 text-xs text-[#64748B] italic whitespace-pre-wrap">“{e.note}”</p>}
        {e.response_photo_path && (
          <div className="mt-1.5" data-testid={`activity-response-photo-${e.event_id}`}>
            <img src={`${API}${e.response_photo_path}`} alt="Recipient-provided" className="h-20 w-20 rounded-lg object-cover border border-[#E2E8F0]" />
            <p className="mt-0.5 text-[10px] text-[#94A3B8]">Recipient-provided photo (not part of your evidence)</p>
          </div>
        )}
        <p className="mt-0.5 text-[10px] text-[#94A3B8]">{fmtDateTime(e.created_at)}</p>
      </div>
    </li>
  );
}

function WitnessCard({ link: l, events, onCopy, onRevoke }) {
  const revoked = l.status === "revoked";
  const url = `${window.location.origin}/witness/${l.slug}`;
  const linkEvents = (events || []).filter((e) => e.link_id === l.link_id);
  const attestedEvt = linkEvents.find((e) => e.event_type === "attested");
  let tone = "gray", label = "Invited";
  if (revoked) { tone = "gray"; label = "Revoked"; }
  else if (attestedEvt) { tone = "green"; label = "Attested"; }
  else if (l.first_viewed_at) { tone = "blue"; label = "Viewed"; }
  return (
    <div data-testid={`share-link-${l.link_id}`}
      className={`rounded-xl border p-3 bg-white ${attestedEvt ? "border-[#A7F3D0]" : "border-[#E2E8F0]"} ${revoked ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]"><UserCheck size={13} className="text-brand" /> Witness link</span>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>
      <p className={`mt-1 text-xs font-mono truncate ${revoked ? "line-through text-[#94A3B8]" : "text-[#475569]"}`} data-testid={`share-url-${l.link_id}`}>{url}</p>
      <div className="mt-2 text-xs" data-testid={`witness-status-${l.link_id}`}>
        {revoked ? <span className="text-[#94A3B8]">Revoked {fmtDate(l.revoked_at)}</span>
          : attestedEvt ? <span className="inline-flex items-center gap-1.5 text-[#177245] font-medium"><UserCheck size={13} /> {attestedEvt.signer_name || "A witness"} recorded a statement {fmtDateTime(attestedEvt.created_at)}</span>
          : l.first_viewed_at ? <span className="inline-flex items-center gap-1.5 text-[#1E40AF] font-medium">Viewed {fmtDate(l.first_viewed_at)} — awaiting statement</span>
          : <span className="inline-flex items-center gap-1.5 text-[#94A3B8]"><EyeOff size={13} /> Invited — not viewed yet</span>}
      </div>
      {attestedEvt?.note && <p className="mt-1 text-xs text-[#334155] italic whitespace-pre-wrap" data-testid={`witness-note-${l.link_id}`}>“{attestedEvt.note}”</p>}
      {!revoked && (
        <div className="mt-2 flex items-center gap-1.5 justify-end">
          <Button data-testid={`copy-link-${l.link_id}`} size="sm" variant="outline" onClick={() => onCopy(l)} className="rounded-lg h-8"><Copy size={14} /></Button>
          <RevokeButton linkId={l.link_id} onRevoke={onRevoke} />
        </div>
      )}
    </div>
  );
}

function StatusPill({ tone, children }) {
  const tones = {
    green: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]",
    blue: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
    gray: "bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${tones[tone]}`}>{children}</span>;
}

function RevokeButton({ linkId, onRevoke }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button data-testid={`revoke-link-${linkId}`} size="sm" variant="ghost" className="rounded-lg h-8 text-[#B91C1C]"><Ban size={14} className="mr-1" /> Revoke</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke this link?</AlertDialogTitle>
          <AlertDialogDescription>The landlord will lose access immediately. This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`confirm-revoke-${linkId}`} onClick={() => onRevoke(linkId)} className="bg-[#B91C1C] hover:bg-[#7F1D1D]">Revoke</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ReportCard({ link: l, events, onCopy, onRevoke }) {
  const revoked = l.status === "revoked";
  const url = `${window.location.origin}/report/${l.slug}`;
  // Response summary derived from the existing normalized event source (no new storage).
  const mine = (events || []).filter((e) => e.link_id === l.link_id);
  const responses = mine.filter((e) => !e.superseded &&
    ["item_acknowledged", "item_disputed", "item_response"].includes(e.event_type));
  const counts = responses.reduce((a, e) => { const k = e.response_label || "Response"; a[k] = (a[k] || 0) + 1; return a; }, {});
  let summary;
  if (responses.length) summary = Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(" · ");
  else if (l.first_viewed_at) summary = "Viewed · no response";
  else summary = "Not viewed";
  return (
    <div data-testid={`share-link-${l.link_id}`}
      className={`rounded-xl border p-3 bg-white border-[#E2E8F0] ${revoked ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]"><FileText size={13} className="text-brand" /> Full report</span>
        <StatusPill tone={revoked ? "gray" : "green"}>{revoked ? "Revoked" : "Active"}</StatusPill>
      </div>
      <p className={`mt-1 text-xs font-mono truncate ${revoked ? "line-through text-[#94A3B8]" : "text-[#475569]"}`} data-testid={`share-url-${l.link_id}`}>{url}</p>
      <span data-testid={`response-summary-${l.link_id}`}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-2.5 py-1 text-[11px] font-semibold text-[#475569]">
        {summary}
      </span>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="text-xs" data-testid={`view-receipt-${l.link_id}`}>
          {revoked ? <span className="text-[#94A3B8]">Revoked {fmtDate(l.revoked_at)}</span>
            : l.first_viewed_at ? <span className="inline-flex items-center gap-1.5 text-[#177245] font-medium"><CheckCircle2 size={13} /> First viewed {fmtDateTime(l.first_viewed_at)} · {l.view_count} views</span>
            : <span className="inline-flex items-center gap-1.5 text-[#94A3B8]"><EyeOff size={13} /> Not viewed yet</span>}
          {!revoked && l.acknowledged_at && (
            <span className="mt-1 flex items-center gap-1.5 text-[#177245] font-semibold" data-testid={`receipt-confirmed-${l.link_id}`}><CheckCircle2 size={13} /> Landlord confirmed receipt on {fmtDate(l.acknowledged_at)}</span>
          )}
        </div>
        {!revoked && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button data-testid={`copy-link-${l.link_id}`} size="sm" variant="outline" onClick={() => onCopy(l)} className="rounded-lg h-8"><Copy size={14} /></Button>
            <RevokeButton linkId={l.link_id} onRevoke={onRevoke} />
          </div>
        )}
      </div>
      {!revoked && <ShareImagePrivacy propertyId={l.property_id} linkId={l.link_id} />}
    </div>
  );
}

function BaselineCard({ link: l, onCopy, onRevoke, onResend, resending, expanded, onToggle }) {
  const revoked = l.status === "revoked";
  const signed = l.landlord_signed;
  const url = `${window.location.origin}/baseline/${l.slug}`;

  let statusEl;
  if (revoked) statusEl = <StatusPill tone="gray">Revoked</StatusPill>;
  else if (signed) statusEl = <StatusPill tone="green"><Lock size={11} className="mr-1" /> Baseline locked</StatusPill>;
  else if (l.first_viewed_at) statusEl = <StatusPill tone="blue">Awaiting acknowledgment</StatusPill>;
  else statusEl = <StatusPill tone="gray">Sent — not viewed</StatusPill>;

  return (
    <div data-testid={`share-link-${l.link_id}`}
      className={`rounded-xl border p-3 bg-white ${signed ? "border-[#A7F3D0]" : "border-[#E2E8F0]"} ${revoked ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]"><ShieldCheck size={13} className="text-brand" /> Baseline acknowledgment <InfoTip topic="baseline" /></span>
        {statusEl}
      </div>
      <p className={`mt-1 text-xs font-mono truncate ${revoked ? "line-through text-[#94A3B8]" : "text-[#475569]"}`} data-testid={`share-url-${l.link_id}`}>{url}</p>

      <div className="mt-2 text-xs" data-testid={`view-receipt-${l.link_id}`}>
        {revoked ? <span className="text-[#94A3B8]">Revoked {fmtDate(l.revoked_at)}</span>
          : signed ? <span className="inline-flex items-center gap-1.5 text-[#177245] font-medium"><CheckCircle2 size={13} /> Acknowledged by {l.landlord_legal_name} on {fmtDateTime(l.signature_timestamp)}</span>
          : l.first_viewed_at ? <span className="inline-flex items-center gap-1.5 text-[#1E40AF] font-medium">Viewed {fmtDate(l.first_viewed_at)} — awaiting acknowledgment</span>
          : <span className="inline-flex items-center gap-1.5 text-[#94A3B8]"><EyeOff size={13} /> Sent — not viewed</span>}
      </div>

      {signed && l.notification_failed && (
        <div className="mt-2 rounded-lg bg-[#FEF3C7] border border-[#FDE68A] px-3 py-2 flex items-center justify-between gap-2" data-testid={`notif-failed-${l.link_id}`}>
          <span className="text-xs text-[#92400E] inline-flex items-center gap-1.5"><AlertTriangle size={13} /> Confirmation email failed to send.</span>
          <Button data-testid={`resend-notif-${l.link_id}`} size="sm" variant="outline" disabled={resending} onClick={() => onResend(l.link_id)} className="rounded-lg h-7 text-xs border-[#FDE68A]">
            {resending ? <Loader2 size={13} className="mr-1 animate-spin" /> : null} Resend
          </Button>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        {signed ? (
          <button data-testid={`toggle-baseline-details-${l.link_id}`} onClick={onToggle}
            className="text-xs text-brand font-medium inline-flex items-center gap-1 hover:underline">
            <Images size={13} /> View notes & photos ({l.counter_photo_count || 0})
            <ChevronRight size={13} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>
        ) : <span />}
        {!revoked && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button data-testid={`copy-link-${l.link_id}`} size="sm" variant="outline" onClick={() => onCopy(l)} className="rounded-lg h-8"><Copy size={14} /></Button>
            {!signed && <RevokeButton linkId={l.link_id} onRevoke={onRevoke} />}
          </div>
        )}
      </div>

      {signed && expanded && (
        <div className="mt-3 border-t border-[#E2E8F0] pt-3 space-y-3" data-testid={`baseline-details-${l.link_id}`}>
          {l.landlord_notes ? (
            <div><p className="text-[11px] font-semibold text-navy">Landlord notes</p><p className="mt-1 text-xs text-[#334155] whitespace-pre-wrap">{l.landlord_notes}</p></div>
          ) : <p className="text-xs text-[#94A3B8]">No notes added by the landlord.</p>}
          {(l.counter_photos || []).length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-navy">Photos added by recipient</p>
              <p className="text-[10px] text-[#94A3B8]">Submitted by the recipient — separate from your sealed evidence.</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {l.counter_photos.map((c) => (
                  <a key={c.id} href={`${API}${c.image_path}`} target="_blank" rel="noreferrer" className="block" data-testid={`counter-photo-${c.id}`}>
                    <img src={`${API}${c.image_path}`} alt={c.caption || "recipient photo"}
                      className="aspect-square w-full object-cover rounded-lg border border-dashed border-[#CBD5E1]" />
                    <p className="mt-0.5 text-[10px] text-[#94A3B8] truncate">Added by recipient{c.uploaded_at ? ` on ${fmtDate(c.uploaded_at)}` : ""}</p>
                    {c.caption && <p className="text-[10px] text-[#64748B] truncate">{c.caption}</p>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
